import { PaymentClass } from 'generated/graphql';

import { cardReferenceForMenuX } from 'helpers/offlineTerminal/cardReferenceFromTerminal';

import { encodeEcrPaymentMeta } from 'helpers/offlineTerminal/ecrPaymentStorage';

import { mapCardBrandToCreditCardType } from 'helpers/offlineTerminal/mapCardBrandToMenuX';

import { OfflineTerminalPayResponse } from 'helpers/offlineTerminal/offlineTerminalTypes';



type PaymentFormData = {

	defaultPayment?: Array<Record<string, unknown>>;

	cardReference?: unknown;

	cardType?: unknown;

};



function isCreditPaymentRow(row: Record<string, unknown>): boolean {
	return (
		Boolean(row?.selected) &&
		String(row?.paymentClass || '').toUpperCase() === PaymentClass.Credit
	);
}



function buildTerminalPaymentFields(result: OfflineTerminalPayResponse) {

	const creditCardType = mapCardBrandToCreditCardType(result.cardType);

	const cardRef = cardReferenceForMenuX(result.maskedCardNo);

	const referenceNo = result.referenceNo || result.approvalCode || '';

	const ecrCheckout = encodeEcrPaymentMeta({

		invoiceNumber: result.invoiceNumber,

		approvalCode: result.approvalCode,

	});



	return { creditCardType, cardRef, referenceNo, ecrCheckout };

}



/**

 * Merge terminal approval into form data for onSubmit().

 * Required because ECR mode hides card inputs — cardRef is not registered in react-hook-form.

 */

export function mergeTerminalResultIntoFormData<T extends PaymentFormData>(

	formData: T,

	result: OfflineTerminalPayResponse,

): T {

	const { creditCardType, cardRef, referenceNo, ecrCheckout } =

		buildTerminalPaymentFields(result);



	const defaultPayment = (formData?.defaultPayment ?? []).map(row => {

		if (!isCreditPaymentRow(row)) {

			return row;

		}



		return {

			...row,

			...(cardRef ? { cardRef } : {}),

			...(referenceNo ? { referenceNo } : {}),

			...(ecrCheckout ? { checkoutID: ecrCheckout } : {}),

			...(result.invoiceNumber

				? { terminalInvoiceNo: result.invoiceNumber }

				: {}),

			...(result.approvalCode

				? { terminalApprovalCode: result.approvalCode }

				: {}),

			...(creditCardType ? { creditCardType } : {}),

		};

	});



	const keepHotelAccount =
		formData?.cardType != null &&
		typeof formData.cardType === 'object' &&
		!!(formData.cardType as { RegistrationID?: string }).RegistrationID;

	return {

		...formData,

		defaultPayment,

		...(cardRef ? { cardReference: cardRef } : {}),

		...(!keepHotelAccount && creditCardType ? { cardType: creditCardType } : {}),

	};

}



/**

 * After terminal approves payment, copy the result into the payment form

 * so the existing onSubmit() in BillSettlement can save to MenuX as usual.

 */

export function applyTerminalResultToPaymentForm(

	setValue: (name: string, value: unknown) => void,

	formData: PaymentFormData,

	result: OfflineTerminalPayResponse,

): void {

	const merged = mergeTerminalResultIntoFormData(formData, result);

	const { creditCardType, cardRef, referenceNo, ecrCheckout } =

		buildTerminalPaymentFields(result);



	const keepHotelAccount =
		formData?.cardType != null &&
		typeof formData.cardType === 'object' &&
		!!(formData.cardType as { RegistrationID?: string }).RegistrationID;

	if (creditCardType && !keepHotelAccount) {

		setValue('cardType', creditCardType);

	}

	if (cardRef) {

		setValue('cardReference', cardRef);

	}



	const payments = merged?.defaultPayment ?? [];

	payments.forEach((row, index) => {

		if (!isCreditPaymentRow(row)) {

			return;

		}

		const prefix = `defaultPayment.${index}`;

		if (cardRef) {

			setValue(`${prefix}.cardRef`, cardRef);

		}

		if (referenceNo) {

			setValue(`${prefix}.referenceNo`, referenceNo);

		}

		if (ecrCheckout) {

			setValue(`${prefix}.checkoutID`, ecrCheckout);

		}

		if (result.invoiceNumber) {

			setValue(`${prefix}.terminalInvoiceNo`, result.invoiceNumber);

		}

		if (result.approvalCode) {

			setValue(`${prefix}.terminalApprovalCode`, result.approvalCode);

		}

		if (creditCardType) {

			setValue(`${prefix}.creditCardType`, creditCardType);

		}

	});

	setValue('defaultPayment', merged.defaultPayment);

}



/**

 * Sum of credit card payment amounts selected in the dialog.

 */

export function getSelectedCreditPaymentAmount(

	formData: { defaultPayment?: Array<Record<string, unknown>> },

): number {

	return (formData?.defaultPayment ?? [])

		.filter(row => isCreditPaymentRow(row))

		.reduce((sum, row) => sum + Number(row?.amountPayment ?? 0), 0);

}



/** True when user selected Credit card payment (not hotel credit account) */

export function shouldUseOfflineTerminalForPayment(

	enabled: boolean,

	formData: { defaultPayment?: Array<Record<string, unknown>> },

	cardTypeValue?: unknown,

): boolean {

	if (!enabled) return false;



	const hasCreditPayment = (formData?.defaultPayment ?? []).some(row =>

		isCreditPaymentRow(row),

	);

	if (!hasCreditPayment) return false;



	// Hotel "charge to credit account" uses an object with CreditBalance — not the card terminal

	const isHotelCreditAccount =

		cardTypeValue != null &&

		typeof cardTypeValue === 'object' &&

		(cardTypeValue as { CreditBalance?: number }).CreditBalance != null;



	return !isHotelCreditAccount;

}


